import { SCREEN_PATHS } from '@/shared/routes';

/* The information architecture, as data.
 *
 * **Seven groups replacing three, and the change is a regrouping rather than a rename.** The previous
 * shell had Catalog / Quality / Platform, and Quality had grown to seventeen entries — which is the
 * flat list the redesign exists to remove. Seventeen items under one heading is not navigation; it is
 * a list somebody reads top to bottom every time.
 *
 * The grouping principle is **what a reader is trying to do**, not which module owns the code. That is
 * why remediation, bulk changes, what-if and autonomous are together under Action even though they
 * come from four different backend modules: all four *change something*, and a steward looking for
 * "how do I fix this" should find them in one place. Equally, contracts and residency sit under
 * Governance rather than Quality — a contract is a promise to a counterparty and residency is a legal
 * constraint, and neither measures data.
 *
 * Every entry resolves to a route that exists in `SCREEN_PATHS`; `navigation.test.ts` asserts the two
 * agree in both directions, so a route added without a nav entry fails the build rather than becoming
 * unreachable, and an entry pointing at a deleted route fails too. That check is the reason this file
 * is data rather than JSX.
 *
 * Four judgements worth not reversing:
 *
 * * **Lineage is under Data, not Quality.** It answers "what is this connected to", which is a
 *   question about the catalogue's contents. Grouping it with rules and scores would suggest it
 *   measures something, and it measures nothing.
 * * **PII review is under Data.** It is a queue about *columns*, and a steward reaches it from the
 *   catalogue rather than from a quality score.
 * * **Alert routing is under Quality, beside incidents.** It answers the question the incident queue
 *   raises — an incident nobody was told about looks identical to one nobody had to be told about.
 * * **Model routing is under AI, not Platform.** It was under Platform when it shipped because it
 *   reads as deployment configuration; it is the decision every AI feature depends on, and an
 *   administrator looking for it is thinking about the AI plane.
 */

export interface NavEntry {
  readonly to: string;
  readonly labelKey: string;
}

export interface NavGroup {
  /** Stable id, used as the persistence key for the expanded/collapsed state. */
  readonly id: string;
  readonly labelKey: string;
  readonly entries: readonly NavEntry[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  /* **Home is a group of one, not a bare link above the groups.** It was the second, and it made the
   * top of the sidebar the only place where a row sat with no heading over it — so the eye had to
   * learn two layouts before reaching Connections. A group of one costs a heading and buys a single
   * rhythm down the whole rail. */
  {
    id: 'home',
    labelKey: 'nav.section.home',
    entries: [{ to: SCREEN_PATHS.home, labelKey: 'nav.home' }],
  },
  {
    id: 'data',
    labelKey: 'nav.section.data',
    entries: [
      { to: SCREEN_PATHS.connections, labelKey: 'nav.connections' },
      { to: SCREEN_PATHS.datasets, labelKey: 'nav.datasets' },
      { to: SCREEN_PATHS.profiles, labelKey: 'nav.profiles' },
      { to: SCREEN_PATHS.lineage, labelKey: 'nav.lineage' },
      { to: SCREEN_PATHS.review, labelKey: 'nav.review' },
    ],
  },
  {
    id: 'quality',
    labelKey: 'nav.section.quality',
    entries: [
      { to: SCREEN_PATHS.rules, labelKey: 'nav.rules' },
      { to: SCREEN_PATHS.executions, labelKey: 'nav.executions' },
      { to: SCREEN_PATHS.scores, labelKey: 'nav.scores' },
      /* Anomalies before incidents: an anomaly is what an incident is opened *from*, so the reading
       * order is cause then consequence. */
      { to: SCREEN_PATHS.anomalies, labelKey: 'nav.anomalies' },
      { to: SCREEN_PATHS.incidents, labelKey: 'nav.incidents' },
      { to: SCREEN_PATHS.alertRouting, labelKey: 'nav.alertRouting' },
      { to: SCREEN_PATHS.reconciliation, labelKey: 'nav.reconciliation' },
    ],
  },
  {
    id: 'governance',
    labelKey: 'nav.section.governance',
    entries: [
      { to: SCREEN_PATHS.policy, labelKey: 'nav.policy' },
      { to: SCREEN_PATHS.compliance, labelKey: 'nav.compliance' },
      { to: SCREEN_PATHS.contracts, labelKey: 'nav.contracts' },
    ],
  },
  {
    id: 'action',
    labelKey: 'nav.section.action',
    entries: [
      { to: SCREEN_PATHS.remediation, labelKey: 'nav.remediation' },
      { to: SCREEN_PATHS.bulk, labelKey: 'nav.bulk' },
      { to: SCREEN_PATHS.autonomous, labelKey: 'nav.autonomous' },
    ],
  },
  {
    id: 'ai',
    labelKey: 'nav.section.ai',
    entries: [
      { to: SCREEN_PATHS.copilot, labelKey: 'nav.copilot' },
      { to: SCREEN_PATHS.suggestions, labelKey: 'nav.suggestions' },
      { to: SCREEN_PATHS.evals, labelKey: 'nav.evals' },
      { to: SCREEN_PATHS.serving, labelKey: 'nav.serving' },
    ],
  },
  {
    id: 'platform',
    labelKey: 'nav.section.platform',
    entries: [
      { to: SCREEN_PATHS.marketplace, labelKey: 'nav.marketplace' },
      { to: SCREEN_PATHS.audit, labelKey: 'nav.audit' },
    ],
  },
  /* **Administration is its own group at the bottom, not scattered through the other six.**
   * `Team` used to sit in Platform; access policy is in Governance, model routing in AI, quotas in
   * Platform - so an administrator setting up a tenant had no single place to go. This group is the
   * front door, and the existing screens keep their own routes: `/admin` links to them rather
   * than duplicating them as tabs, which would give one screen two routes.
   *
   * **That argument is now applied to Connectors and Score model too, which used to sit here as
   * tabs beside the front door that already linked to them.** Both were the two-doors shape the
   * comment above rejects, and removing the tabs leaves the door `/admin` already provides. */
  {
    id: 'admin',
    labelKey: 'nav.section.admin',
    entries: [
      { to: SCREEN_PATHS.admin, labelKey: 'nav.admin' },
      /* **Ticketing is in the rail, and the objection that kept it out does not apply here.**
       * It was reachable only from the chip row on `/admin`, a screen titled "Users & access" —
       * so connecting a service desk meant going somewhere named for a different subject, and the
       * rail then highlighted "Users & access" while a reader was on Ticketing. The recorded
       * argument against an entry was that "a sidebar entry would put it in front of a steward
       * every day for something they do not do", and `ADMIN_ONLY_GROUPS` hides this whole group
       * from a non-admin, so it is in front of nobody but the person who does it.
       *
       * Six of the eight screens the `/admin` chip row links to already have rail entries —
       * Connections, Model routing, Alert routing, Quotas, Access policy, Deployment. Ticketing
       * was the outlier rather than the rule. Score model is still out and is left alone: its own
       * argument is about scoring rather than about this group. */
      { to: SCREEN_PATHS.integrations, labelKey: 'nav.integrations' },
    ],
  },
];

/** Groups that are deliberately one entry long, each with the argument that admits it.
 *
 * **A registry rather than a loosened bound**, for the reason `NOT_IN_NAV` and
 * `PLATFORM_SCOPED_TABLES` are registries: "a group of one is not worth a heading" is a good rule
 * and this is a real exception to it, so the exception is written down where a reviewer sees it
 * beside the next one. Dropping the assertion instead would let the *accidental* single-entry group
 * — a section somebody split and never filled — back in silently.
 */
export const SINGLE_ENTRY_GROUPS: Readonly<Record<string, string>> = {
  /* `admin` was here and is not any more: Ticketing joined it, so the heading stands over two
   * entries and needs no exemption. The entry is removed rather than left behind, because a
   * registry naming a group that no longer qualifies exempts nothing and hides the next one —
   * which is the stale-entry rule `DELIBERATE_ENUM_NARROWINGS` is checked against on the
   * backend. */
  home:
    'The landing page. It was an entry above the groups and became a group of one so the rail has ' +
    'one rhythm from top to bottom — a reader should not have to learn two layouts before reaching ' +
    'Connections. The heading costs a line and buys that.',
};

/* **No expanded/collapsed state any more, and no keys to persist it.** The grouped sidebar this
 * replaced let a reader collapse groups and remembered which; the redesign's rail shows eight tiles
 * and the current group's entries as tabs, so there is nothing left to collapse. The three storage
 * keys went with it rather than lingering — a key nothing writes is a key somebody reads stale. */

/** Groups for routes that sit outside the rail, each with the group whose tabs should show there.
 *
 * A detail page needs no entry: `/datasets/:id` already sits under `/datasets` and the prefix match
 * finds it. These are the screens with no rail entry *and* no parent path in a group — reached from
 * Admin's front door — so without a mapping the top bar would show the Overview's tabs on them. The
 * groups are the design's (`OFF_RAIL` in its app controller). */
export const OFF_RAIL_GROUPS: Readonly<Record<string, string>> = {
  [SCREEN_PATHS.metering]: 'platform',
  [SCREEN_PATHS.platform]: 'platform',
  [SCREEN_PATHS.team]: 'admin',
};

/** Which group holds a location: the group of the longest rail entry the path sits under, an
 * off-rail mapping, or Home.
 *
 * **Longest prefix, bounded on the separator**, so `/admin/integrations` belongs to Ticketing's group
 * through its own entry rather than through `/admin`, and `/administration` would match neither. The
 * root matches only itself, or every path would sit under Home. */
export function groupForPath(
  pathname: string,
  groups: readonly NavGroup[] = NAV_GROUPS,
): string {
  let best: { readonly id: string; readonly length: number } | null = null;
  for (const group of groups) {
    for (const entry of group.entries) {
      const hit =
        entry.to === '/'
          ? pathname === '/'
          : pathname === entry.to || pathname.startsWith(`${entry.to}/`);
      if (hit && (best === null || entry.to.length > best.length)) {
        best = { id: group.id, length: entry.to.length };
      }
    }
  }
  if (best !== null) return best.id;
  const offRail = Object.entries(OFF_RAIL_GROUPS).find(
    ([path]) => pathname === path || pathname.startsWith(`${path}/`),
  );
  return offRail ? offRail[1] : 'home';
}

/** Entries that must match their route exactly, derived rather than listed.
 *
 * **A rail entry whose path is a prefix of another entry's path lights up on that other entry.**
 * `NavLink` matches by prefix unless told otherwise, which is right for `/datasets/:id` keeping
 * Datasets marked — nothing in the rail names that child. It is wrong the moment a child *does*
 * have its own entry, because then two entries claim one location: on `/admin/integrations` the
 * rail marked both Ticketing and `Users & access`, telling a reader they were on a screen about
 * user accounts.
 *
 * The root was the only case when this was written and was special-cased at the call site. It is
 * the same case — `/` is a prefix of every path — so one derived rule covers both, and the next
 * child entry added under an existing one needs no second edit. That is the registry-versus-rule
 * choice this file already makes for `SINGLE_ENTRY_GROUPS`, decided the other way because this one
 * is *derivable* and a group's exemption is an argument somebody has to write.
 *
 * **Bounded on the separator**, so `/admin` is a prefix of `/admin/integrations` and would not be
 * one of a hypothetical `/administration` — the whole-segment rule `LUMIQ_LLM_KEY_ENDPOINTS` and
 * the alert-routing allow-list are both matched with on the backend, for the same reason: a bare
 * `startsWith` quietly captures a sibling.
 */
export function exactMatchEntries(paths: readonly string[]): ReadonlySet<string> {
  return new Set(
    paths.filter((path) => {
      const boundary = path.endsWith('/') ? path : `${path}/`;
      return paths.some((other) => other !== path && other.startsWith(boundary));
    }),
  );
}

/* **A function over a list, not an expression over `NAV_GROUPS`**, and that is not a style choice.
 * The first version computed the set inline, so the separator bound could only be tested by a test
 * that reimplemented it — which proved the *test's* copy was bounded and said nothing about the
 * code. Removing the bound from the real derivation left all three tests green, because no path in
 * the rail is currently a sibling-prefix of another: the bound is protection against the *next*
 * route, which is exactly the kind a vacuous test leaves unguarded. Taking the list as an argument
 * is what lets a test feed it `['/admin', '/administration']` and get a real answer. */
export const EXACT_MATCH_ENTRIES: ReadonlySet<string> = exactMatchEntries(
  NAV_GROUPS.flatMap((group) => group.entries).map((entry) => entry.to),
);
